/**
 * VaultDashboard - Main Robinhood-style vault page
 *
 * Features:
 * - Animated portfolio value
 * - Interactive chart with scrubbing
 * - Gamification stats (streaks, win rate, ATH, milestones)
 * - Pull to refresh
 * - Celebration animations
 * - Desktop/Mobile responsive layouts
 */

import React, { useCallback, lazy, Suspense } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { VaultProvider, useVault } from '../../contexts/VaultContext';
import { FeedbackProvider, useFeedback } from '../../contexts/FeedbackContext';
import { useDesktopView } from '../../hooks/useDesktopView';
import { useAutoDepositNotifications } from '../../hooks/useAutoDepositNotifications';
import AnimatedPortfolioValue from './AnimatedPortfolioValue';
import InteractiveChart from './InteractiveChart';
import StatCards from './StatCards';
import CelebrationManager from './CelebrationManager';
import PullToRefresh from './PullToRefresh';
import ExposureDashboard from './ExposureDashboard';
import ActivityModal from './ActivityModal';

// Lazy load desktop components for better mobile performance
const DesktopVaultDashboard = lazy(() => import('./desktop/DesktopVaultDashboard'));

// Settings modal trigger
interface SettingsButtonProps {
  onClick: () => void;
}

const SettingsButton: React.FC<SettingsButtonProps> = ({ onClick }) => {
  return (
    <motion.button
      onClick={onClick}
      className="p-2 rounded-full bg-gray-800 hover:bg-gray-700 transition-colors"
      whileTap={{ scale: 0.95 }}
    >
      <svg
        className="w-5 h-5 text-gray-400"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"
        />
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
        />
      </svg>
    </motion.button>
  );
};

// Notification bell
interface NotificationBellProps {
  count: number;
  onClick: () => void;
}

const NotificationBell: React.FC<NotificationBellProps> = ({ count, onClick }) => {
  return (
    <motion.button
      onClick={onClick}
      className="p-2 rounded-full bg-gray-800 hover:bg-gray-700 transition-colors relative"
      whileTap={{ scale: 0.95 }}
    >
      <svg
        className="w-5 h-5 text-gray-400"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9"
        />
      </svg>
      {count > 0 && (
        <motion.span
          className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs font-bold rounded-full flex items-center justify-center"
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ type: 'spring', damping: 10 }}
        >
          {count > 9 ? '9+' : count}
        </motion.span>
      )}
    </motion.button>
  );
};

// Last updated indicator
interface LastUpdatedProps {
  date: Date | null;
  isRefreshing: boolean;
}

const LastUpdated: React.FC<LastUpdatedProps> = ({ date, isRefreshing }) => {
  const formatTime = (d: Date) => {
    const now = new Date();
    const diff = now.getTime() - d.getTime();
    const seconds = Math.floor(diff / 1000);
    const minutes = Math.floor(seconds / 60);

    if (minutes < 1) return 'Just now';
    if (minutes === 1) return '1 min ago';
    if (minutes < 60) return `${minutes} mins ago`;

    return d.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
    });
  };

  return (
    <div className="text-center text-xs text-gray-500 mt-4">
      {isRefreshing ? (
        <span className="flex items-center justify-center gap-1">
          <motion.span
            className="w-3 h-3 border-2 border-gray-500 border-t-white rounded-full"
            animate={{ rotate: 360 }}
            transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
          />
          Refreshing...
        </span>
      ) : date ? (
        <span>Updated {formatTime(date)}</span>
      ) : null}
    </div>
  );
};

// Loading fallback for desktop dashboard
const DesktopLoadingFallback: React.FC = () => (
  <div className="min-h-screen bg-black text-white flex items-center justify-center">
    <div className="flex flex-col items-center gap-4">
      <motion.div
        className="w-10 h-10 border-3 border-gray-600 border-t-white rounded-full"
        animate={{ rotate: 360 }}
        transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
      />
      <p className="text-gray-500">Loading desktop view...</p>
    </div>
  </div>
);

// Mobile dashboard content
const MobileVaultDashboard: React.FC = () => {
  const { refresh, isRefreshing, lastUpdated, unreadNotifications, error } = useVault();
  const { soundEnabled, setSoundEnabled, hapticEnabled, setHapticEnabled, playSound } = useFeedback();

  const [showSettings, setShowSettings] = React.useState(false);
  const [showNotifications, setShowNotifications] = React.useState(false);
  const [showActivity, setShowActivity] = React.useState(false);

  // Auto-deposit notifications
  useAutoDepositNotifications({
    enabled: true,
    pollInterval: 30000,
    onAutoDeposit: () => {
      // Refresh dashboard data when auto-deposit is detected
      refresh();
      playSound('success');
    },
  });

  const handleRefresh = useCallback(async () => {
    await refresh();
  }, [refresh]);

  return (
    <CelebrationManager>
      <div className="min-h-screen bg-black text-white">
        <PullToRefresh onRefresh={handleRefresh} className="h-screen">
          <div className="px-4 py-6 pb-20">
            {/* Header */}
            <header className="flex items-center justify-between mb-6">
              <h1 className="text-xl font-semibold">Vault</h1>
              <div className="flex items-center gap-2">
                <NotificationBell
                  count={unreadNotifications}
                  onClick={() => setShowNotifications(true)}
                />
                <SettingsButton onClick={() => setShowSettings(true)} />
              </div>
            </header>

            {/* Error message */}
            {error && (
              <motion.div
                className="bg-red-500/10 border border-red-500/30 rounded-lg p-3 mb-4"
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
              >
                <p className="text-red-400 text-sm">{error}</p>
              </motion.div>
            )}

            {/* Portfolio Value */}
            <AnimatedPortfolioValue className="mb-6" />

            {/* Chart */}
            <InteractiveChart className="mb-6" height={220} />

            {/* Stat Cards */}
            <StatCards className="mb-6" />

            {/* Exposure Dashboard */}
            <ExposureDashboard className="mb-6" variant="mobile" />

            {/* Last Updated */}
            <LastUpdated date={lastUpdated} isRefreshing={isRefreshing} />
          </div>
        </PullToRefresh>

        {/* Settings Modal */}
        {showSettings && (
          <SettingsModal
            onClose={() => setShowSettings(false)}
            soundEnabled={soundEnabled}
            setSoundEnabled={setSoundEnabled}
            hapticEnabled={hapticEnabled}
            setHapticEnabled={setHapticEnabled}
          />
        )}

        {/* Notifications Modal */}
        {showNotifications && (
          <NotificationsModal onClose={() => setShowNotifications(false)} />
        )}

        {/* Activity Modal */}
        <ActivityModal
          isOpen={showActivity}
          onClose={() => setShowActivity(false)}
        />

        {/* Bottom Navigation */}
        <BottomNavigation onActivityClick={() => setShowActivity(true)} />
      </div>
    </CelebrationManager>
  );
};

// Inner dashboard content with responsive switching
const VaultDashboardContent: React.FC = () => {
  const { isDesktop } = useDesktopView();

  // Desktop view
  if (isDesktop) {
    return (
      <Suspense fallback={<DesktopLoadingFallback />}>
        <DesktopVaultDashboard />
      </Suspense>
    );
  }

  // Mobile view
  return <MobileVaultDashboard />;
};

// Settings Modal
interface SettingsModalProps {
  onClose: () => void;
  soundEnabled: boolean;
  setSoundEnabled: (enabled: boolean) => void;
  hapticEnabled: boolean;
  setHapticEnabled: (enabled: boolean) => void;
}

const SettingsModal: React.FC<SettingsModalProps> = ({
  onClose,
  soundEnabled,
  setSoundEnabled,
  hapticEnabled,
  setHapticEnabled,
}) => {
  return (
    <motion.div
      className="fixed inset-0 bg-black/80 z-50 flex items-end"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      onClick={onClose}
    >
      <motion.div
        className="bg-gray-900 w-full rounded-t-2xl p-6"
        initial={{ y: '100%' }}
        animate={{ y: 0 }}
        exit={{ y: '100%' }}
        onClick={e => e.stopPropagation()}
      >
        <div className="w-12 h-1 bg-gray-700 rounded-full mx-auto mb-6" />

        <h2 className="text-xl font-semibold mb-6">Settings</h2>

        <div className="space-y-4">
          <ToggleSetting
            label="Sound Effects"
            description="Play sounds for interactions"
            enabled={soundEnabled}
            onChange={setSoundEnabled}
          />
          <ToggleSetting
            label="Haptic Feedback"
            description="Vibrate on interactions"
            enabled={hapticEnabled}
            onChange={setHapticEnabled}
          />
        </div>

        <button
          onClick={onClose}
          className="w-full mt-6 py-3 bg-white text-black font-medium rounded-lg"
        >
          Done
        </button>
      </motion.div>
    </motion.div>
  );
};

// Toggle Setting component
interface ToggleSettingProps {
  label: string;
  description: string;
  enabled: boolean;
  onChange: (enabled: boolean) => void;
}

const ToggleSetting: React.FC<ToggleSettingProps> = ({
  label,
  description,
  enabled,
  onChange,
}) => {
  return (
    <div className="flex items-center justify-between">
      <div>
        <p className="font-medium">{label}</p>
        <p className="text-sm text-gray-400">{description}</p>
      </div>
      <button
        onClick={() => onChange(!enabled)}
        className={`w-12 h-7 rounded-full p-1 transition-colors ${
          enabled ? 'bg-green-500' : 'bg-gray-600'
        }`}
      >
        <motion.div
          className="w-5 h-5 bg-white rounded-full"
          animate={{ x: enabled ? 20 : 0 }}
          transition={{ type: 'spring', damping: 20, stiffness: 300 }}
        />
      </button>
    </div>
  );
};

// Notifications Modal placeholder
const NotificationsModal: React.FC<{ onClose: () => void }> = ({ onClose }) => {
  return (
    <motion.div
      className="fixed inset-0 bg-black/80 z-50 flex items-end"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      onClick={onClose}
    >
      <motion.div
        className="bg-gray-900 w-full h-3/4 rounded-t-2xl p-6 overflow-y-auto"
        initial={{ y: '100%' }}
        animate={{ y: 0 }}
        exit={{ y: '100%' }}
        onClick={e => e.stopPropagation()}
      >
        <div className="w-12 h-1 bg-gray-700 rounded-full mx-auto mb-6" />

        <h2 className="text-xl font-semibold mb-6">Notifications</h2>

        <p className="text-gray-400 text-center py-8">
          No notifications yet
        </p>

        <button
          onClick={onClose}
          className="w-full py-3 bg-white text-black font-medium rounded-lg"
        >
          Close
        </button>
      </motion.div>
    </motion.div>
  );
};

// Bottom Navigation Bar
interface BottomNavigationProps {
  onActivityClick?: () => void;
}

const BottomNavigation: React.FC<BottomNavigationProps> = ({ onActivityClick }) => {
  const location = useLocation();
  const navigate = useNavigate();

  const navItems = [
    {
      path: '/vault',
      label: 'Vault',
      icon: (
        <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 12l3-3 3 3 4-4M8 21l4-4 4 4M3 4h18M4 4h16v12a1 1 0 01-1 1H5a1 1 0 01-1-1V4z" />
        </svg>
      ),
    },
    {
      path: '/bot',
      label: 'Bot',
      icon: (
        <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
        </svg>
      ),
    },
    {
      path: 'activity',
      label: 'Activity',
      isAction: true,
      icon: (
        <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      ),
    },
    {
      path: '/wallet',
      label: 'Wallet',
      icon: (
        <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
        </svg>
      ),
    },
    {
      path: '/settings',
      label: 'Settings',
      icon: (
        <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
        </svg>
      ),
    },
  ];

  return (
    <nav className="fixed bottom-0 left-0 right-0 bg-gray-900 border-t border-gray-800 z-40 pb-safe">
      <div className="flex justify-around py-2">
        {navItems.map(item => {
          const isActive = !item.isAction && location.pathname === item.path;
          return (
            <motion.button
              key={item.path}
              onClick={() => {
                if (item.isAction && item.path === 'activity') {
                  onActivityClick?.();
                } else {
                  navigate(item.path);
                }
              }}
              className={`flex flex-col items-center p-2 min-w-[64px] ${
                isActive ? 'text-white' : 'text-gray-500'
              }`}
              whileTap={{ scale: 0.95 }}
            >
              {item.icon}
              <span className="text-xs mt-1">{item.label}</span>
              {isActive && (
                <motion.div
                  className="absolute bottom-1 w-1 h-1 bg-white rounded-full"
                  layoutId="navIndicator"
                />
              )}
            </motion.button>
          );
        })}
      </div>
    </nav>
  );
};

// Main VaultDashboard with providers
const VaultDashboard: React.FC = () => {
  return (
    <FeedbackProvider>
      <VaultProvider>
        <VaultDashboardContent />
      </VaultProvider>
    </FeedbackProvider>
  );
};

export default VaultDashboard;
